import {Button, Dropdown, DropdownProps, MenuProps} from 'antd';
import {MoreOutlined} from '@ant-design/icons';
import React, {useMemo, useState} from 'react';
import {ModelDetailDTO, ModelDTO} from 'hemwb-api';
import {SubModelSection} from '../../../router/routes';
import {Run16} from '@carbon/icons-react';
import {isModelExternal, isModelTheorem} from '../../../../store/model';
import ModelEditMetadataActionMenuButton from '../common/ModelEdit/ModelEditMetadata/ModelEditMetadataActionMenuButton';
import ModelEditPermissionsActionMenuButton from '../common/ModelEdit/ModelEditPermissons/ModelEditPermissionsActionMenuButton';
import ModelShareSideBarModalMenuButton from '../common/ModelShareSideBarModal/ModelShareSideBarModalMenuButton';
import ModelCloneActionMenuButton from '../common/ModelCloneSideBarModal/ModelCloneActionMenuButton';
import ModelDownloadActionMenuButton from '../common/ModelDownload/ModelDownloadActionMenuButton';
import PublishModelActionMenuButton from '../common/ModelPublish/PublishModelActionMenuButton';
import ModelDeleteDraftSideBarModalMenuButton from '../common/ModelDeleteDraftSideBarModal/ModelDeleteDraftSideBarModalMenuButton';
import ModelRetireActionMenuButton from '../common/ModelRetire/ModelRetireActionMenuButton';
import ModelEditFilesActionMenuButton from '../common/ModelEdit/ModelEditFiles/ModelEditFilesActionMenuButton';
import SubModelListActionMenuLink from '../../subModel/common/SubModelActionMenu/SubModelListActionMenuLink';
import VisualizationCreateActionMenuLink from '../../visualization/common/VisualizationCreateActionMenuLink/VisualizationCreateActionMenuLink';
import ModelOverviewRequestAccess from '../common/ModelOverview/ModelOverviewRequestAccess';
import styles from './ModelItemActionMenu.module.scss';
import {ButtonType} from 'antd/es/button';
import {
  canCloneModel,
  canDeleteDraftModel,
  canDownloadModelFiles,
  canEditModel,
  canPublishModel,
  canRetireModel,
  checkExternalSecurityAPI,
  PERMISSION,
} from '../../../../permissions/usePermissions';

export const ActionsCell = React.memo(({model}: {model: ModelDetailDTO}) => {
  const isTHModel = isModelTheorem(model);
  const hasPermission = isTHModel ? checkExternalSecurityAPI(model) : canDownloadModelFiles(model);

  return (
    <div style={{display: 'flex', justifyContent: 'flex-end'}}>
      {hasPermission ? (
        <ModelDownloadActionMenuButton model={model} displayAntDesign={true} />
      ) : (
        !isTHModel && <ModelOverviewRequestAccess model={model} displayAntDesign={true} type="link" />
      )}
    </div>
  );
});

export const OperationCell = ({
  model,
  buttonType,
  placement,
  tourId,
}: {
  model: ModelDTO;
  buttonType?: ButtonType;
  placement?: DropdownProps['placement'];
  tourId?: string;
}) => {
  const [open, setOpen] = useState(false);

  const handleMenuClick: MenuProps['onClick'] = (info) => {
    info.domEvent.stopPropagation();

    // Close dropdown immediately - position is already captured in openModal
    setOpen(false);
  };

  const isWebModel = model.metadata.platform === 'WebModel 2.0';
  const externalModel = isModelExternal(model);
  const canEdit = canEditModel(model);
  const canClone = canCloneModel(model);
  const canPublish = canPublishModel(model as ModelDetailDTO);
  const canRetire = canRetireModel(model);
  const canDeleteDraft = canDeleteDraftModel(model);
  const canDownload = isModelTheorem(model)
    ? checkExternalSecurityAPI(model as ModelDetailDTO)
    : canDownloadModelFiles(model as ModelDetailDTO);

  const items: MenuProps['items'] = useMemo(
    () => [
      {type: 'divider'},
      // Execution Section - only for WebModel 2.0
      ...(isWebModel
        ? [
            {
              key: 'execution-group',
              type: 'group' as const,
              label: (
                <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <Run16 />
                  Execution
                </span>
              ),
            },
            {type: 'divider' as const},
            {
              key: 'scenario',
              label: <SubModelListActionMenuLink model={model} section={SubModelSection.SCENARIO} foreignContext />,
            },
            {
              key: 'callibration',
              label: <SubModelListActionMenuLink model={model} section={SubModelSection.CALIBRATION} foreignContext />,
            },
            {
              key: 'display',
              label: <SubModelListActionMenuLink model={model} section={SubModelSection.DISPLAY} foreignContext />,
            },
            {
              key: 'visualization',
              label: <VisualizationCreateActionMenuLink model={model} />,
            },
            {type: 'divider' as const},
          ]
        : []),
      // Editing Section
      ...(canEdit === PERMISSION.ALLOWED
        ? [
            {
              key: 'files',
              label: <ModelEditFilesActionMenuButton model={model} newDesign={true} />,
            },
            ...(externalModel
              ? []
              : [
                  {
                    key: 'metadata',
                    label: <ModelEditMetadataActionMenuButton model={model} newDesign={true} />,
                  },
                ]),
            {
              key: 'permissions',
              label: <ModelEditPermissionsActionMenuButton model={model} newDesign={true} />,
            },
          ]
        : []),

      // Actions Section
      ...(canClone === PERMISSION.ALLOWED && !externalModel
        ? [
            {
              key: 'newModel',
              label: <ModelCloneActionMenuButton model={model} newDesign={true} />,
            },
          ]
        : []),
      ...(canPublish === PERMISSION.ALLOWED
        ? [
            {
              key: 'publish',
              label: <PublishModelActionMenuButton model={model as ModelDetailDTO} />,
            },
          ]
        : []),
      ...(canDownload === PERMISSION.ALLOWED
        ? [
            {
              key: 'download',
              label: <ModelDownloadActionMenuButton model={model as ModelDetailDTO} fromSideBar={true} />,
            },
          ]
        : []),
      {
        key: 'share',
        label: <ModelShareSideBarModalMenuButton model={model} newDesign={true} />,
      },

      // Danger Zone Section
      ...(canDeleteDraft === PERMISSION.ALLOWED
        ? [
            {
              key: 'deleteDraft',
              label: <ModelDeleteDraftSideBarModalMenuButton model={model} newDesign={true} />,
            },
          ]
        : []),
      ...(canRetire === PERMISSION.ALLOWED && !externalModel
        ? [
            {
              key: 'retire',
              label: <ModelRetireActionMenuButton model={model} newDesign={true} />,
            },
          ]
        : []),
    ],
    [model, isWebModel, externalModel, canEdit, canClone, canPublish, canDownload, canDeleteDraft, canRetire],
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      menu={{
        items,
        onClick: handleMenuClick,
      }}
      trigger={['click']}
      placement={placement ?? 'bottomRight'}
      overlayClassName={styles.dropdownMenu}
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
      autoAdjustOverflow={true}
      destroyPopupOnHide={false}
      align={{
        overflow: {adjustX: true, adjustY: true},
      }}
    >
      <Button
        type={buttonType ?? 'text'}
        data-tour-id={tourId}
        icon={<MoreOutlined />}
        onClick={(e) => e.stopPropagation()}
      />
    </Dropdown>
  );
};
